import React, { useState } from 'react'
import { Typography, Modal, Divider, message, Select, Form, Input, InputNumber } from 'antd'
import * as dateFns from 'date-fns'
import DatePicker, { disabledDateBefore, disabledTimeBefore } from "./DatePicker"
import { isEndDateValid } from "../../utils/formValidation"
import MappingEditor from "./MappingEditor"
import VpsWhitelistEditor from "./VpsWhitelistEditor"
import DuplicateCheckEditor from "./DuplicateCheckEditor"
import { useAuth } from "../../authContext"
const { Title, Text } = Typography

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const ISIN_RE = /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/

const validateFields = (fields) => {
  const seen = new Set()
  const seenPdf = new Set()
  for (const f of fields) {
    if (!f.key || !/^[a-zA-Z][a-zA-Z0-9_]*$/.test(f.key)) return `Invalid key "${f.key || '(empty)'}"`
    if (!f.label || !f.label.trim()) return `Field "${f.key}" needs a label`
    if (seen.has(f.key)) return `Duplicate key "${f.key}"`
    seen.add(f.key)
    if (f.type === 'dropdown' && (!Array.isArray(f.options) || f.options.filter(o => o && o.trim()).length === 0)) {
      return `Dropdown field "${f.key}" needs at least one option`
    }
    if (f.pdfField) {
      if (seenPdf.has(f.pdfField)) return `PDF field "${f.pdfField}" mapped more than once`
      seenPdf.add(f.pdfField)
    }
  }
  return null
}

const DocumentEditModal = ({document, onOk, ...props}) => {
  const { isSudo } = useAuth()
  const [author, setAuthor] = useState(() => (document && document.author) || '')
  const [endDate, setEndDate] = useState(() => document && document.endDate ? new Date(document.endDate) : null)
  const [fields, setFields] = useState(() => (document && document.fields) || [])
  const [formTitle, setFormTitle] = useState(() => (document && document.formTitle) || '')
  const [formIntro, setFormIntro] = useState(() => (document && document.formIntro) || '')
  const [allowedEmails, setAllowedEmails] = useState(() => (document && document.allowedEmails) || [])
  const [vpsSource, setVpsSource] = useState(() => (document && document.vpsSource) || null)
  const [vpsWhitelist, setVpsWhitelist] = useState(() => (document && document.vpsWhitelist) || null)
  const [duplicateCheck, setDuplicateCheck] = useState(() => (document && document.duplicateCheck) || null)
  const [isin, setIsin] = useState(() => (document && document.isin) || '')
  const [lopenummerStart, setLopenummerStart] = useState(() => (document && document.lopenummerStart) || null)

  // Once a signature has consumed a number from the sequence, the start
  // number is locked so a change can never make two signatures collide.
  const lopenummerLocked = !!(document && document.lopenummerStart != null &&
    document.lopenummerNext != null && document.lopenummerNext !== document.lopenummerStart)

  const handleDateChange = (value) => {
    const dateValue = value ? dateFns.setSeconds(value, 0) : null
    isEndDateValid(dateValue)
    setEndDate(dateValue)
  }

  if (!document) {
    return null
  }

  const handleOk = () => {
    const err = validateFields(fields)
    if (err) {
      message.error(err)
      return
    }
    const normalizedEmails = []
    for (const raw of allowedEmails) {
      const v = (raw || '').trim().toLowerCase()
      if (!v) continue
      if (!EMAIL_RE.test(v)) {
        message.error(`"${raw}" is not a valid email`)
        return
      }
      if (!normalizedEmails.includes(v)) normalizedEmails.push(v)
    }

    const trimmedIsin = (isin || '').trim().toUpperCase()
    if (trimmedIsin && !ISIN_RE.test(trimmedIsin)) {
      message.error('ISIN must be 12 characters: 2 letters, 9 letters/digits, 1 check digit')
      return
    }

    const update = { endDate, fields, formTitle, formIntro, allowedEmails: normalizedEmails, vpsWhitelist, duplicateCheck, isin: trimmedIsin }

    // Only sent when actually changed — the server rejects `author` outright
    // for anyone without the elevated role.
    if (isSudo) {
      const nextAuthor = (author || '').trim().toLowerCase()
      if (!nextAuthor || !EMAIL_RE.test(nextAuthor)) {
        message.error('Owner must be a valid email address')
        return
      }
      if (nextAuthor !== (document.author || '')) {
        update.author = nextAuthor
      }
    }

    if (!lopenummerLocked && lopenummerStart != null && lopenummerStart !== '') {
      if (!Number.isInteger(lopenummerStart) || lopenummerStart < 1) {
        message.error('Løpenummer start must be a positive whole number')
        return
      }
      update.lopenummerStart = lopenummerStart
    }

    onOk(update)
  }

  return (
    <Modal
      title={`Edit document: ${document.name || ''}`}
      okText="Update"
      width={960}
      onOk={handleOk}
      {...props}
    >
      {isSudo && (
        <>
          <Title level={5}>Owner</Title>
          <Text type="secondary">
            The colleague who owns this deal and sees it in their document list. Deals uploaded
            before the Entra ID migration have an old internal id here instead of an email — set it
            to the real owner's address to restore it to their list.
            {document.authorEmail && document.authorEmail.toLowerCase() !== (document.author || '').toLowerCase()
              ? ` Uploaded by ${document.authorEmail}.`
              : ''}
          </Text>
          <Form layout="vertical" style={{ marginTop: 12 }}>
            <Form.Item>
              <Input
                value={author}
                onChange={e => setAuthor(e.target.value)}
                placeholder="colleague@arctic.com"
                style={{ maxWidth: 360 }}
              />
            </Form.Item>
          </Form>

          <Divider />
        </>
      )}

      <Title level={5}>Document expiration time</Title>
      <p>Optional, if set users can not sign the document after the selected date and time</p>
      <DatePicker
        value={endDate}
        onChange={value => handleDateChange(value)}
        showTime={{
          format: "HH:mm"
        }}
        disabledDate={disabledDateBefore}
        disabledTime={disabledTimeBefore}
        allowClear
      />

      <Divider />

      <Title level={5}>ISIN</Title>
      <Text type="secondary">Required for VPS acceptance-form export. The same ISIN is used for every entry in the export.</Text>
      <Form layout="vertical" style={{ marginTop: 12 }}>
        <Form.Item>
          <Input
            value={isin}
            onChange={e => setIsin(e.target.value.toUpperCase())}
            placeholder="e.g. NO0010911902"
            maxLength={12}
            style={{ maxWidth: 200 }}
          />
        </Form.Item>
      </Form>

      <Title level={5}>Løpenummer start number</Title>
      <Text type="secondary">
        {lopenummerLocked
          ? `Locked: signatures on this deal already use this sequence (next number would be ${document.lopenummerNext}). It can no longer be changed.`
          : 'Required for VPS acceptance-form export. Each signature that completes from now on gets the next number in sequence. Leave blank to not assign Løpenummer for this deal.'}
      </Text>
      <Form layout="vertical" style={{ marginTop: 12 }}>
        <Form.Item>
          <InputNumber
            value={lopenummerStart}
            onChange={value => setLopenummerStart(value)}
            disabled={lopenummerLocked}
            min={1}
            precision={0}
            placeholder="e.g. 10001"
            style={{ maxWidth: 200 }}
          />
        </Form.Item>
      </Form>

      <Divider />

      <Title level={5}>Shared with</Title>
      <Text type="secondary">
        These colleagues can see, edit, export and delete this document in the admin table. You (the uploader) always have access.
      </Text>
      <Form layout="vertical" style={{ marginTop: 12 }}>
        <Form.Item>
          <Select
            mode="tags"
            tokenSeparators={[',', ' ', ';']}
            placeholder="colleague@arctic.com, another@arctic.com"
            value={allowedEmails}
            onChange={(vals) => setAllowedEmails(vals)}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Form>

      <Divider />

      <MappingEditor
        pdfFieldNames={document.pdfFieldNames || []}
        fields={fields}
        formTitle={formTitle}
        formIntro={formIntro}
        onChange={(next) => {
          setFields(next.fields)
          setFormTitle(next.formTitle)
          setFormIntro(next.formIntro)
        }}
      />

      <Divider />

      <VpsWhitelistEditor
        docId={document._id}
        fields={fields}
        vpsSource={vpsSource}
        vpsWhitelist={vpsWhitelist}
        onSourceChange={(doc) => {
          setVpsSource(doc.vpsSource || null)
          setVpsWhitelist(doc.vpsWhitelist || null)
        }}
        onChange={(wl) => setVpsWhitelist(wl)}
      />

      <Divider />

      <DuplicateCheckEditor
        fields={fields}
        value={duplicateCheck}
        defaultVpsFieldKey={vpsWhitelist && vpsWhitelist.fieldKey}
        onChange={(dc) => setDuplicateCheck(dc)}
      />
    </Modal>
  )
}

export default DocumentEditModal
